import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { Pagination } from "@mui/material";
import { FaUserFriends, FaFileAlt } from "react-icons/fa"; // Import Crew Icon
import CircularProgress from '@mui/material/CircularProgress';

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Modal,
  Tooltip
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "../../Navbar/Navbar";
import baseURL from "../../config";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const CrewProfileTable = () => {
  const [crewProfiles, setCrewProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCrew, setTotalCrew] = useState(0)
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Loader state
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  // Fetch crew profiles
  const fetchCrewProfiles = async (page = 1, search = "") => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${baseURL}/api/getcrewprofile`, {
        params: { page, limit: itemsPerPage, search },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrewProfiles(response.data.data);
      setTotalPages(response.data.totalPages);
      setTotalCrew(response.data.total)
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Only show loader when changing page
      if (searchTerm === "") setIsLoading(true);

      await fetchCrewProfiles(currentPage, searchTerm);
      setIsLoading(false);
    };

    fetchData();
  }, [currentPage, searchTerm]);


  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchCrewProfiles(pageNumber, searchTerm);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEdit = (crewProfile) => {
    navigate("/crewprofile",
      { state: { crewProfile, from: "CrewProfileTable" } });
  };

  const handleDocumentClick = (documents) => {
    setSelectedDocument(
      Object.entries(documents)
        .filter(([_, doc]) => doc?.filePath)
        .map(([key, doc]) => {
          let filePath = doc.filePath.replace(/\\/g, "/");

          // Check if the filePath is already a full URL (e.g., hosted on S3)
          if (!filePath.startsWith("http")) {
            // If the file is stored locally, use your backend URL
            if (filePath.startsWith("/Documents")) {
              filePath = `${backendBaseURL}${filePath}`;
            } else {
              filePath = `${baseURL}/${filePath}`;
            }
          }

          return {
            name: key,
            fileName: doc.fileName || key,
            filePath: filePath,
          };
        })
    );
    setModalOpen(true);
  };

  const GenerateResume = async (id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${baseURL}/api/generate-cv/${id}`, {
        responseType: "blob", // Important to get the file as binary
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Trigger file download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Optional: You could get filename from response headers if you send it
      a.download = `Allinz_Crew_Resume.docx`;
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading resume:", error);
      alert("Failed to generate resume. Try again.");
    }
  };

  const GenerateZMIResume = async (id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${baseURL}/api/generate-zmi-cv/${id}`, {
        responseType: "blob", // Important to get the file as binary
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Trigger file download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Optional: You could get filename from response headers if you send it
      a.download = `ZMI_Crew_Resume.docx`;
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading resume:", error);
      alert("Failed to generate resume. Try again.");
    }
  };

  return (
    <>
      <div>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '16rem',
            }}
          >
            <CircularProgress sx={{ backgroundColor: "#C30B12" }} size={60} thickness={5} />
          </Box>

        ) : (
          <div>
            <Navbar />
            {/* Error message */}
            {error && (
              <Box p={4} textAlign="center" bgcolor="#fdecea" color="#b71c1c">
                <Typography color="error" variant="h6">
                  {error}
                </Typography>
              </Box>
            )}
              <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
  <Typography variant="h5" display="flex" alignItems="center" gutterBottom>
    <FaUserFriends style={{ marginRight: "8px", color: "#C30B12" }} />
    Crew Profile Table
  </Typography>
</Box>

            <Box p={4}>
              <div className="crewProfileTable-container">

                {/* Table Header: Title, Search, and Add Button */}
                <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                  {/* Search Bar */}
                  <TextField
                    label="Search by Crew Name / Indos No"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ maxWidth: 300, backgroundColor: "#f5f5f5" }}
                  />

                  <Box>
                    <Link to="/AssignedCrew">
                      <Button variant="contained" style={{ backgroundColor: "#C30B12" }}>Assigned Crew to Crewing Agent</Button>
                    </Link>
                  </Box>

                  <Box>
                    <Link to="/Crewcvs">
                      <Button variant="contained" style={{ backgroundColor: "#C30B12" }}>Crew CV</Button>
                    </Link>
                  </Box>
                  <Box>
                    <Link to="/Listofvacancy">
                      <Button variant="contained" style={{ backgroundColor: "#C30B12" }}>All Vacancy</Button>
                    </Link>
                  </Box>

                  <Box>
                    <Link to="/MatchedVacanciesForCVs">
                      <Button variant="contained" style={{ backgroundColor: "#C30B12" }}>Crew Matched Vacancies</Button>
                    </Link>
                  </Box>
               

                  {/* Buttons Wrapper */}
                  <Box display="flex" gap={1}>
                    <Link to="/crewprofile">
                      <Button variant="contained" sx={{ backgroundColor: "#C30B12" }}>Add Crew</Button>
                    </Link>
                    <Link to="/RankTable">
                      <Button variant="contained" sx={{ backgroundColor: "#C30B12" }}>Ranks</Button>
                    </Link>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, padding: "6px 12px", bgcolor: "#fff3cd", borderRadius: "8px" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#856404" }}>
                        Total Crew:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#d39e00" }}>
                        {totalCrew}
                      </Typography>
                    </Box>
                  </Box>
                </Box>


                <Paper>
                  <Table sx={{ borderRadius: "8px" }}>
                    <TableHead sx={{ backgroundColor: "#C30B12", height: "30px", borderRadius: "8px" }}>
                      <TableRow>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                          <Typography sx={{ color: "white", fontWeight: "bold" }}>Sr. No.</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>Personal Info</TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>Info</TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>Documents</TableCell>
                        <TableCell sx={{ color: "white", fontWeight: "bold" }}>Edit</TableCell>
                        {/* <TableCell sx={{ color: "white", fontWeight: "bold" }}>Action</TableCell> */}
                      </TableRow>

                    </TableHead>
                    <TableBody>
                      {crewProfiles.length > 0 ? (
                        crewProfiles.map((profile, index) => (
                          <TableRow key={profile._id}>
                            <TableCell sx={{ fontSize: "0.85rem", padding: "5px" }}>{index + 1 + (currentPage - 1) * itemsPerPage}</TableCell>
                            <TableCell sx={{ padding: "5px" }}>
                              <Typography sx={{ fontSize: "0.85rem" }}>
                                <strong>Name:</strong>{" "}
                                {profile.name
                                  ? profile.name
                                    .toLowerCase()
                                    .replace(/\b\w/g, (char) => char.toUpperCase())
                                  : "N/A"}
                              </Typography>

                              <Typography sx={{ fontSize: "0.85rem" }}><strong>Indos No:</strong> {profile.indosno}</Typography>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>Email:</strong> {profile.email}</Typography>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>Mobile No.:</strong> {profile.mobile}</Typography>
                            </TableCell>

                            <TableCell>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>Rank Name:</strong> {profile.rankname}</Typography>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>Purpose:</strong> {profile.purpose}</Typography>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>CDC:</strong> {profile.cdc}</Typography>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>COC:</strong> {profile.coc}</Typography>
                              <Typography sx={{ fontSize: "0.85rem" }}><strong>CVC Category:</strong> {profile.cvcategory}</Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.85rem", padding: "5px" }}>
                              {profile &&
                                Object.keys(profile).some((key) =>
                                  key.toLowerCase().includes("document") && profile[key] !== null
                                ) ? (
                                <IconButton onClick={() => handleDocumentClick(profile)}>
                                  <PictureAsPdfIcon sx={{ color: "red" }} />
                                </IconButton>
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  No Document Available
                                </Typography>
                              )}
                            </TableCell>


                            <TableCell sx={{ fontSize: "0.85rem", padding: "5px" }}>
                              <IconButton onClick={() => handleEdit(profile)} sx={{ color: "#C30B12", "&:hover": { color: "darkblue" } }}>
                                <FaEdit sx={{ color: "blue" }} />
                              </IconButton>
                            </TableCell>
                            {/* <TableCell sx={{ fontSize: "0.85rem", padding: "5px" }}>
                        <Tooltip title="Download Allianz CV">
                          <IconButton onClick={() => GenerateResume(profile._id)}>
                            <FaFileAlt />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download ZMI CV">
                          <IconButton onClick={() => GenerateZMIResume(profile._id)}>
                            <FaFileAlt />
                          </IconButton>
                        </Tooltip>

                      </TableCell> */}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ fontSize: "0.85rem", padding: "5px" }}>
                            <Typography align="center">No crew profiles found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                {/* Pagination Controls */}
                <Box mt={4} display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, page) => handlePageChange(page)}
                    variant="outlined"
                    shape="rounded"


                  />
                </Box>
              </div>
              <ToastContainer />

              {/* PDF Modal */}
              {/* PDF Modal */}
              <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: "60%", md: "50%" },
                    maxHeight: "80vh",
                    bgcolor: "white",
                    boxShadow: 24,
                    p: 3,
                    borderRadius: 2,
                    overflowY: "auto",
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ textAlign: "center", mb: 2, fontWeight: "bold" }}>
                    Crew Documents
                  </Typography>
                  {selectedDocument.length > 0 ? (
                    selectedDocument
                      .filter((doc) =>
                        // Check both name and fileName properties, and ensure neither includes visa or ticket
                        !(doc.name?.includes("visaDocument") || doc.fileName?.includes("visaDocument")) &&
                        !(doc.name?.includes("ticketDocument") || doc.fileName?.includes("ticketDocument"))
                      )
                      .map((doc, index) => (
                        <Paper
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            mb: 2,
                            boxShadow: 2,
                            borderRadius: 2,
                            transition: "0.3s",
                            "&:hover": { boxShadow: 4, bgcolor: "#f5f5f5" },
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            <PictureAsPdfIcon sx={{ color: "red", mr: 1 }} />
                            <Typography variant="body1">{doc.fileName}</Typography>
                          </Box>
                          <Button
                            variant="contained"
                            color="primary"
                            href={doc.filePath}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ textTransform: "none" }}
                          >
                            Download
                          </Button>
                        </Paper>
                      ))
                  ) : (
                    <Typography align="center" color="textSecondary">
                      No documents available
                    </Typography>
                  )}
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button variant="contained" color="error" onClick={() => setModalOpen(false)}>
                      Close
                    </Button>
                  </Box>
                </Box>
              </Modal>
            </Box>

          </div>
        )}
      </div>
    </>
  );
};

export default CrewProfileTable;
